package co.maytes.standupbot.dashboard.features.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.events.Event

enum class ThemePreference(val value: String) {
    System("system"),
    Light("light"),
    Dark("dark");

    companion object {
        fun fromValue(raw: String?): ThemePreference = when (raw) {
            Light.value -> Light
            Dark.value -> Dark
            else -> System
        }
    }
}

private const val STORAGE_KEY = "sb-theme"

class ThemeStore {
    val preference: MutableState<ThemePreference> = mutableStateOf(readStoredPreference())

    fun setPreference(next: ThemePreference) {
        preference.value = next
        writeStoredPreference(next)
        applyThemeToBody(resolveEffectiveTheme(next))
    }

    fun applyInitial() {
        applyThemeToBody(resolveEffectiveTheme(preference.value))
    }

    fun effectiveTheme(): EffectiveTheme = resolveEffectiveTheme(preference.value)

    fun toggleLightDark() {
        val next = if (effectiveTheme() == EffectiveTheme.Dark) ThemePreference.Light else ThemePreference.Dark
        setPreference(next)
    }

    private fun resolveEffectiveTheme(pref: ThemePreference): EffectiveTheme = when (pref) {
        ThemePreference.Light -> EffectiveTheme.Light
        ThemePreference.Dark -> EffectiveTheme.Dark
        ThemePreference.System -> if (systemPrefersDark()) EffectiveTheme.Dark else EffectiveTheme.Light
    }
}

enum class EffectiveTheme(val datasetValue: String) {
    Light("light"),
    Dark("dark"),
}

val LocalThemeStore = compositionLocalOf<ThemeStore> {
    error("ThemeStore not provided")
}

@Composable
fun rememberThemeStore(): ThemeStore = remember { ThemeStore().also { it.applyInitial() } }

@Composable
fun SystemThemeListener(store: ThemeStore) {
    DisposableEffect(Unit) {
        val mql = try {
            window.matchMedia("(prefers-color-scheme: dark)")
        } catch (_: Throwable) {
            null
        }
        val listener: (Event) -> Unit = {
            if (store.preference.value == ThemePreference.System) {
                store.applyInitial()
            }
        }
        mql?.addEventListener("change", listener)
        onDispose {
            mql?.removeEventListener("change", listener)
        }
    }
}

private const val LEGACY_STORAGE_KEY = "theme"

private fun readStoredPreference(): ThemePreference = try {
    val raw = localStorage.getItem(STORAGE_KEY) ?: localStorage.getItem(LEGACY_STORAGE_KEY)
    ThemePreference.fromValue(raw)
} catch (_: Throwable) {
    ThemePreference.System
}

private fun writeStoredPreference(pref: ThemePreference) {
    try {
        localStorage.setItem(STORAGE_KEY, pref.value)
    } catch (_: Throwable) {
    }
}

private fun systemPrefersDark(): Boolean = try {
    window.matchMedia("(prefers-color-scheme: dark)").matches
} catch (_: Throwable) {
    false
}

private fun applyThemeToBody(effective: EffectiveTheme) {
    try {
        val body = window.document.body ?: return
        body.setAttribute("data-theme", effective.datasetValue)
    } catch (_: Throwable) {
    }
}
